import { getSafeExternalUrl, LinkPreview } from "./linkPreviewModel";

export interface LegacyPreviewResult {
	originalUrl?: unknown;
	url?: unknown;
	title?: unknown;
	description?: unknown;
	images?: unknown;
}

export type LinkPreviewMetadataResolver = (
	urls: string[],
) => Promise<LegacyPreviewResult[]>;

const BLOCKED_TITLES = [
	/^access denied\b/i,
	/^attention required\b/i,
	/^checking your browser\b/i,
	/^just a moment\b/i,
];

const nullableText = (value: unknown): string | null => {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	return trimmed || null;
};

const urlKey = (value: unknown): string | null => {
	const safeUrl = getSafeExternalUrl(value);
	if (!safeUrl) return null;
	try {
		return new URL(safeUrl).toString();
	} catch (_error) {
		return null;
	}
};

const getWwwFallbackUrl = (value: unknown): string | null => {
	const safeUrl = getSafeExternalUrl(value);
	if (!safeUrl) return null;
	try {
		const parsed = new URL(safeUrl);
		if (!parsed.hostname.toLowerCase().startsWith("www.")) return null;
		parsed.hostname = parsed.hostname.slice(4);
		return parsed.toString();
	} catch (_error) {
		return null;
	}
};

const getUsableImage = (images: unknown): string | null => {
	if (!Array.isArray(images)) return null;
	for (const value of images) {
		const image = getSafeExternalUrl(value);
		if (!image) continue;
		try {
			const parsed = new URL(image);
			if (!parsed.pathname.toLowerCase().includes("placeholder")) {
				return image;
			}
		} catch (_error) {
			// getSafeExternalUrl already validates URLs; continue defensively.
		}
	}
	return null;
};

export const hasLinkPreviewMetadata = (preview: LinkPreview) =>
	Boolean(preview.title || preview.description || preview.image);

const hydratePreview = (
	preview: LinkPreview,
	result: LegacyPreviewResult | undefined,
): LinkPreview | null => {
	if (!result) return null;

	const title = nullableText(result.title);
	if (title && BLOCKED_TITLES.some((pattern) => pattern.test(title))) {
		return null;
	}
	const description = nullableText(result.description);
	const image = getUsableImage(result.images);
	if (!title && !description && !image) return null;

	return {
		...preview,
		title,
		description,
		image,
	};
};

export const hydrateLinkPreviews = async (
	previews: LinkPreview[],
	resolveMetadata: LinkPreviewMetadataResolver,
): Promise<LinkPreview[]> => {
	const unresolved = previews.filter(
		(preview) => !preview.internal && !hasLinkPreviewMetadata(preview),
	);
	if (unresolved.length === 0) {
		return previews.filter(hasLinkPreviewMetadata);
	}

	let results: LegacyPreviewResult[];
	try {
		results = await resolveMetadata(
			Array.from(new Set(unresolved.map(({ url }) => url))),
		);
	} catch (_error) {
		return previews.filter(hasLinkPreviewMetadata);
	}

	const resultsByUrl = new Map<string, LegacyPreviewResult>();
	for (const result of results) {
		const key = urlKey(result.originalUrl) || urlKey(result.url);
		if (key && !resultsByUrl.has(key)) resultsByUrl.set(key, result);
	}

	const fallbackUrls = new Map<string, string>();
	for (const preview of unresolved) {
		const key = urlKey(preview.url);
		const hydrated = hydratePreview(
			preview,
			key ? resultsByUrl.get(key) : undefined,
		);
		const fallbackUrl = hydrated ? null : getWwwFallbackUrl(preview.url);
		if (key && fallbackUrl) fallbackUrls.set(key, fallbackUrl);
	}

	const fallbackResultsByUrl = new Map<string, LegacyPreviewResult>();
	if (fallbackUrls.size > 0) {
		try {
			const fallbackResults = await resolveMetadata(
				Array.from(new Set(fallbackUrls.values())),
			);
			for (const result of fallbackResults) {
				const key = urlKey(result.originalUrl) || urlKey(result.url);
				if (key && !fallbackResultsByUrl.has(key)) {
					fallbackResultsByUrl.set(key, result);
				}
			}
		} catch (_error) {
			// Keep any previews successfully hydrated by the first request.
		}
	}

	return previews.flatMap((preview) => {
		if (hasLinkPreviewMetadata(preview)) return [preview];
		const key = urlKey(preview.url);
		const primary = hydratePreview(
			preview,
			key ? resultsByUrl.get(key) : undefined,
		);
		const fallbackUrl = key ? fallbackUrls.get(key) : undefined;
		const hydrated =
			primary ||
			hydratePreview(
				preview,
				fallbackUrl
					? fallbackResultsByUrl.get(urlKey(fallbackUrl) || "")
					: undefined,
			);
		return hydrated ? [hydrated] : [];
	});
};
